import {
  ScopeOfPracticeEnum,
  ShiftRequestStatusEnum,
  ShiftStatusEnum,
  ShiftTypeEnum
} from '@umss/shared/utils';
import { Location } from './location.model';
import { PagedRequestDto } from './paged-request.model';
import { RateInfo } from './shift-need.model';
import { Shift } from './shift.model';

export type NeedType = 'SHIFT' | 'TEMPORARY' | 'URGENT';
export enum ShiftRequestCounterOfferBy {
  Contractor = 'contractor',
  Facility = 'facility'
}

export interface ShiftRequest extends PagedRequestDto {
  ContractorId: string;
  FacilityId: string;
  ScopeOfPractice: ScopeOfPracticeEnum;
  Status: ShiftStatusEnum;
  ShiftType: ShiftTypeEnum;
  StartDate: string;
  EndDate: string;
  MinHourlyRate: number;
  MaxHourlyRate: number;
}

export interface ShiftUpcomingRequest {
  daysAhead?: number;
  startDate?: string;
  endDate?: string;
}

export interface ShiftCancelRequest {
  reason: string;
  cancellationNote: string;
}

export interface ShiftCheckConflictRequest {
  contractorId: string;
  shiftDate: string;
  startTime: string;
  endTime: string;
}

export interface ShiftRequestContractorDto {
  id: string;
  fullName: string;
  profilePictureUrl?: string;
  scopeOfPractice: ScopeOfPracticeEnum;
  scopeOfPracticeDisplay: string;
  licenseNumber: string;
  yearsExperience?: number;
  averageRating?: number;
  reviewCount: number;
  isVerified: boolean;
  backgroundCheckCompleted: boolean;
}

export interface ShiftRequestDto {
  id: string; // format: uuid
  shiftNeedId: string; // format: uuid
  contractorId: string; // format: uuid

  contractorName: string;

  contractorProfilePictureUrl?: string;
  contractorYearsOfExperience?: number; // format: int32
  contractorRating?: number; // format: double

  contractor: ShiftRequestContractorDto;

  /** null when no counter-offer has been made */
  latestCounterOfferRate?: number | null; // format: double
  latestCounterOfferBy?:
    | ShiftRequestCounterOfferBy
    | `${ShiftRequestCounterOfferBy}`
    | null;
  latestCounterOfferAt?: string | null; // format: date-time
  latestCounterOfferMessage?: string | null;
  /** true iff status === 'negotiating' */
  isNegotiationActive: boolean;

  message?: string;

  status: ShiftRequestStatusEnum;
  requestedAt: string; // format: date-time
  respondedAt?: string; // format: date-time

  responseMessage?: string;
  facilityId?: string; // format: uuid

  facilityName?: string;

  scopeOfPractice: ScopeOfPracticeEnum;

  scopeOfPracticeDisplay?: string;

  shiftType: ShiftTypeEnum;

  shiftTypeDisplay?: string;
  shiftDate?: string; // format: date
  startTime?: string; // format: time
  endTime?: string; // format: time
  shiftNeedHourlyRate?: number; // New meaning: contractor rate (base + bonus)
  bonus?: number | null; // Facility bonus snapshot
  differentialRate?: number | null; // Huro platform margin snapshot
  estimatedHours?: number; // format: double
  facilitySubtypeId?: number; // format: int32

  facilitySubtypeName?: string;
  distanceMiles?: number; // format: double

  facilityLocation: Location;

  facilityFullAddress?: string;

  initiatedBy: 'contractor' | 'facility';
  createdBy: string; // format: uuid

  createdByName?: string;
  estimatedPayment?: number; // format: double

  rateInfo: RateInfo;
  expiresAt?: string; // format: date-time
}

// ─── Counter-Offer / Negotiation DTOs ────────────────────────────────────────

/** Input body for submitting a counter-offer (contractor or facility). */
export interface CounterOfferRequestDto {
  /** Required. Must be between 1.00 and 500.00. */
  proposedRate: number;
  /** Optional, max 500 chars. */
  message?: string;
}

/** A single counter-offer entry as returned by the API. */
export interface CounterOfferDto {
  id: string; // format: uuid
  shiftRequestId: string; // format: uuid
  proposedRate: number; // format: double
  /** "contractor" | "facility" */
  proposedByRole: 'contractor' | 'facility';
  proposedByUserId: string; // format: uuid
  proposedByUserName: string | null;
  message: string | null;
  createdAt: string; // format: date-time
}

/**
 * Full negotiation history for a shift request.
 * GET /api/shift-requests/{requestId}/negotiation-history
 */
export interface NegotiationHistoryDto {
  shiftRequestId: string; // format: uuid
  /** Current status of the shift request (snake_case). */
  status: string;
  /** Derived: status === 'negotiating'. */
  isNegotiationActive: boolean;
  /** Chronological list of all counter-offers exchanged. */
  offers: CounterOfferDto[];
}

/** Payload for the SignalR "NegotiationEnded" event. */
export interface NegotiationEndedEventDto {
  shiftRequestId: string; // format: uuid
  finalStatus: 'accepted' | 'declined' | 'withdrawn';
  /** Populated only when finalStatus === 'accepted'. */
  finalRate: number | null; // format: double
}

// DTO for approve requests response
export interface ApproveRequestsResponseDto {
  approvedShifts: Shift[];
  failureCount: number;
  failures: ApproveRequestsFailureDto[];
  successCount: number;
  totalRequested: number;
}

export interface ApproveRequestsFailureDto {
  requestId: string;
  errorCode: string;
  errorMessage: string;
}

// ============================================================================
// DASHBOARD SHIFT REQUEST DTO
// ============================================================================

/**
 * Shift request with full context for dashboard display.
 * Matches backend ShiftRequestDto structure.
 */
export interface DashboardShiftRequestDto {
  id: string;
  shiftNeedId: string;
  contractorId: string;
  contractorName: string;
  /** null when no counter-offer has been made */
  latestCounterOfferRate?: number | null;
  latestCounterOfferBy?: 'contractor' | 'facility' | null;
  latestCounterOfferAt?: string | null;
  latestCounterOfferMessage?: string | null;
  /** true iff status === 'negotiating' */
  isNegotiationActive?: boolean;
  message?: string;
  status: string;
  requestedAt: string; // ISO datetime
  respondedAt?: string;
  responseMessage?: string;
  facilityId?: string;
  facilityName?: string;
  facilitySubtypeId?: number;
  facilitySubtypeName?: string;
  facilityFullAddress?: string;
  location?: string; // Formatted location string
  distanceMiles?: number;
  scopeOfPractice?: number;
  scopeOfPracticeDisplay?: string;
  shiftType?: string;
  shiftTypeDisplay?: string;
  shiftDate?: string; // YYYY-MM-DD format
  startTime?: string; // HH:mm:ss format
  endTime?: string;
  shiftNeedHourlyRate?: number; // New meaning: contractor rate (base + bonus)
  bonus?: number | null; // Facility bonus snapshot
  differentialRate?: number | null; // Huro platform margin snapshot
  estimatedHours?: number;
  estimatedPayment?: number;
  rateInfo?: RateInfo;
  initiatedBy?: string; // "contractor" or "facility"
  createdBy?: string;
  createdByName?: string;
  expiresAt?: string; // ISO datetime
  numberOfPositions?: number;
  /**
   * True when the facility sent this request even though the shift falls outside
   * the contractor's set availability (they were marked Unavailable at send time).
   * Set by the backend at send time; drives the "outside availability" notice in
   * the contractor's shift-details modal.
   */
  sentOutsideAvailability?: boolean;
}
